// UvsTimeChart.jsx
// SVG line chart for Degree of Consolidation (U, %) vs Time (months)
// Input: series = [{ month:number, Tv:number, U:number }, ...]  (U in percent)

import React from 'react';
import PropTypes from 'prop-types';

// -------- helpers (no hooks) --------
const scaleLinear = (domain, range) => {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const m = (r1 - r0) / (d1 - d0 || 1);
  return (x) => r0 + (x - d0) * m;
};

const makeTicks = (min, max, count = 5) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const span = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(span / count)));
  const err = (count * step) / span;
  const niceStep = err <= 0.15 ? step * 10 : err <= 0.35 ? step * 5 : err <= 0.75 ? step * 2 : step;
  const t0 = Math.ceil(min / niceStep) * niceStep;
  const ticks = [];
  for (let v = t0; v <= max + 1e-9; v += niceStep) ticks.push(v);
  return ticks;
};

function UvsTimeChart({
  series,
  width = '100%',
  height = 300,
  margin = { top: 16, right: 20, bottom: 38, left: 48 },
  showPoints = true,
  strokeWidth = 2,
  lineColor = '#1976d2',
  gridColor = '#eee',
  axisColor = '#333',
  fontSize = 11,
  ariaLabel = 'Degree of consolidation versus time',
}) {
  // sanitize + sort (no hooks)
  const data = Array.isArray(series) ? series.filter((d) => d && Number.isFinite(d.month) && Number.isFinite(d.U)) : [];
  if (data.length === 0) {
    return (
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize="12" fill="#888">
          No data
        </text>
      </svg>
    );
  }
  const sorted = [...data].sort((a, b) => a.month - b.month);

  // domains
  const minMonth = sorted[0].month;
  const maxMonth = sorted[sorted.length - 1].month;
  const minU = 0;
  const maxU = Math.max(100, ...sorted.map((d) => d.U));

  // geometry
  const vbW = typeof width === 'number' ? width : 800; // for viewBox only
  const vbH = height;
  const ix0 = margin.left;
  const iy0 = margin.top;
  const iw = Math.max(10, vbW - margin.left - margin.right);
  const ih = Math.max(10, vbH - margin.top - margin.bottom);

  // scales (no hooks)
  const x = scaleLinear([minMonth, maxMonth], [ix0, ix0 + iw]);
  const y = scaleLinear([minU, maxU], [iy0 + ih, iy0]);

  // path string
  const pathD = sorted.map((d, i) => `${i ? 'L' : 'M'}${x(d.month)},${y(d.U)}`).join(' ');

  // ticks
  const xTicks = makeTicks(minMonth, maxMonth, 6);
  const yTicks = makeTicks(minU, maxU, 5);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${vbW} ${vbH}`}
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'block' }}
    >
      {/* Grid */}
      <g stroke={gridColor}>
        {yTicks.map((v, i) => (
          <line key={`gy-${i}`} x1={ix0} x2={ix0 + iw} y1={y(v)} y2={y(v)} />
        ))}
        {xTicks.map((v, i) => (
          <line key={`gx-${i}`} y1={iy0} y2={iy0 + ih} x1={x(v)} x2={x(v)} />
        ))}
      </g>

      {/* Axes */}
      <g stroke={axisColor}>
        <line x1={ix0} x2={ix0 + iw} y1={iy0 + ih} y2={iy0 + ih} />
        <line x1={ix0} x2={ix0} y1={iy0} y2={iy0 + ih} />
      </g>

      {/* Tick labels + axis titles */}
      <g fill={axisColor} fontSize={fontSize}>
        {xTicks.map((v, i) => (
          <g key={`xt-${i}`} transform={`translate(${x(v)}, ${iy0 + ih})`}>
            <line y1="0" y2="6" stroke={axisColor} />
            <text y="18" textAnchor="middle">
              {Number.isInteger(v) ? v : v.toFixed(1)}
            </text>
          </g>
        ))}
        {yTicks.map((v, i) => (
          <g key={`yt-${i}`} transform={`translate(${ix0}, ${y(v)})`}>
            <line x1="0" x2="-6" stroke={axisColor} />
            <text x="-10" dy="0.35em" textAnchor="end">
              {Number.isInteger(v) ? v : v.toFixed(1)}
            </text>
          </g>
        ))}
        <text x={ix0 + iw / 2} y={iy0 + ih + 30} textAnchor="middle" fontWeight="600">
          Time, t (months)
        </text>
        <text
          x={ix0 - 36}
          y={iy0 + ih / 2}
          textAnchor="middle"
          fontWeight="600"
          transform={`rotate(-90 ${ix0 - 36} ${iy0 + ih / 2})`}
        >
          Degree of consolidation, U (%)
        </text>
      </g>

      {/* Line */}
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth={strokeWidth} />

      {/* Points */}
      {showPoints ? (
        <g>
          {sorted.map((d, i) => (
            <circle key={i} cx={x(d.month)} cy={y(d.U)} r={2.5} fill={axisColor}>
              <title>{`t = ${d.month} month, U = ${d.U.toFixed(2)}%`}</title>
            </circle>
          ))}
        </g>
      ) : null}
    </svg>
  );
}

UvsTimeChart.propTypes = {
  series: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.number.isRequired,
      Tv: PropTypes.number,
      U: PropTypes.number.isRequired,
    })
  ).isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.number,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  showPoints: PropTypes.bool,
  strokeWidth: PropTypes.number,
  lineColor: PropTypes.string,
  gridColor: PropTypes.string,
  axisColor: PropTypes.string,
  fontSize: PropTypes.number,
  ariaLabel: PropTypes.string,
};

// Still fine to memoize the component itself (one-time, unconditional)
export default React.memo(UvsTimeChart);
